import React, { useState } from "react";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Radio,
    RadioGroup,
    TextField,
    Typography,
} from "@mui/material";
import { styled } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import {
    borderRadius,
    colors,
    fontSize,
    fontWeight,
    gaps,
    padding,
} from "theme/defaultStyle";
import ActionButton from "controls/global/action-button/ActionButton";
import { HOLD_SCOPE, PIPActionType } from "utils/data/enum";

const DialogHeading = styled(DialogTitle)({
    display: "flex",
    alignItems: "center",
    gap: gaps.small1,
    fontSize: fontSize.xlarge,
    fontWeight: fontWeight.bold2,
    color: "#000000",
    paddingBottom: 0,
});

const Subtitle = styled(Typography)({
    fontSize: fontSize.xsmall,
    color: colors.grey11,
    paddingLeft: padding.xlarge4,
    paddingRight: padding.large1,
    marginTop: "-4px",
    marginBottom: gaps.small1,
});

const WarningBanner = styled(Box)({
    display: "flex",
    alignItems: "flex-start",
    gap: gaps.small1,
    padding: padding.medium1,
    backgroundColor: "#fcefc3",
    border: "1px solid #e8c068",
    borderRadius: borderRadius.small,
    marginBottom: gaps.medium1,
});

const FieldLabel = styled(Typography)({
    fontSize: fontSize.xxsmall,
    fontWeight: fontWeight.bold2,
    color: colors.grey22,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
    marginBottom: gaps.xsmall1,
    "& span": {
        color: colors.red01,
    },
});

interface BulkActionModalProps {
    open: boolean;
    onClose: () => void;
    recordCount: number;
    tabName: string;
    warningMessage?: string;
    onConfirm: (reason: string, holdScope?: HOLD_SCOPE) => void | Promise<unknown>;
    actionType: PIPActionType
    returnTabName?: string;
    // When provided (single-record hold from the drawer), the modal renders a
    // "Hold Policy" presentation with a "Policy #: <number>" subtitle instead
    // of the bulk "Bulk Hold (N)" wording.
    policyNumber?: string;
    // Informational body text for the REMOVE_HOLD variant (no reason textarea).
    infoMessage?: React.ReactNode;

}

const BulkActionModal: React.FC<BulkActionModalProps> = ({
    open,
    onClose,
    recordCount,
    tabName,
    warningMessage,
    onConfirm,
    actionType,
    returnTabName,
    policyNumber,
    infoMessage
}) => {
    const [reason, setReason] = useState("");
    const [holdScope, setHoldScope] = useState<HOLD_SCOPE>(HOLD_SCOPE.ALL_MATCHING);
    const [isSubmitting, setIsSubmitting] = useState(false);

    React.useEffect(() => {
        if (open) {
            setReason("");
            setHoldScope(HOLD_SCOPE.ALL_MATCHING);
            setIsSubmitting(false);
        }
    }, [open]);

    // Single-record "Hold Policy" mode (drawer) vs bulk mode (toolbar).
    const isSingleHold = actionType === PIPActionType.BULK_HOLD && policyNumber !== undefined;
    // Single-record "Remove Hold" confirmation (drawer): info text, no reason.
    const isRemoveHold = actionType === PIPActionType.REMOVE_HOLD;
    // Single-record "Delete Policy" mode (drawer) vs bulk mode (toolbar).
    const isSingleDelete = actionType === PIPActionType.BULK_DELETE && policyNumber !== undefined;
    // Show the "Policy #: <number>" subtitle for the single-record drawer variants.
    const showPolicyNumber =
        policyNumber !== undefined &&
        (actionType === PIPActionType.BULK_HOLD || isRemoveHold || actionType === PIPActionType.BULK_DELETE);

    let buttonLabel = "";
    switch (actionType) {
        case PIPActionType.BULK_HOLD:
            buttonLabel = isSingleHold ? "Hold Policy" : "Bulk Hold";
            break;
        case PIPActionType.BULK_RETURN:
            buttonLabel = `Bulk Return to ${returnTabName}`;
            break;
        case PIPActionType.REMOVE_HOLD:
            buttonLabel = "Remove Hold";
            break;
        case PIPActionType.BULK_DELETE:
            buttonLabel = isSingleDelete ? "Delete Policy" : "Bulk Delete";
            break;
        default:
            throw new Error(`Invalid action type: ${actionType}`);
    }
    const handleClose = () => {
        if (isSubmitting) return;
        setReason("");
        setHoldScope(HOLD_SCOPE.ALL_MATCHING);
        onClose();
    };

    const handleConfirm = async () => {
        if (isSubmitting) return;
        setIsSubmitting(true);
        try {
            await onConfirm(
                reason.trim(),
                actionType === PIPActionType.BULK_HOLD || isRemoveHold ? holdScope : undefined
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const getHeaderLabel = () => {
        if (actionType === PIPActionType.BULK_HOLD) {
            return (<>
                {isSingleHold ? "Hold Policy" : "Bulk Hold"}
                <IconButton
                    onClick={handleClose}
                    sx={{ marginLeft: "auto" }}
                    size="small"
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </>);
        }

        else if (actionType === PIPActionType.BULK_RETURN) {
            return (<>
                <WarningAmberIcon sx={{ color: "#c49a24", fontSize: 28 }} />
                Bulk Return to {returnTabName}
                <IconButton
                    onClick={handleClose}
                    sx={{ marginLeft: "auto" }}
                    size="small"
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </>);
        }

        else if (actionType === PIPActionType.REMOVE_HOLD) {
            return (<>
                Remove Hold
                <IconButton
                    onClick={handleClose}
                    sx={{ marginLeft: "auto" }}
                    size="small"
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </>);
        }

        else if (actionType === PIPActionType.BULK_DELETE) {
            return (<>
                <WarningAmberIcon sx={{ color: "#c49a24", fontSize: 28 }} />
                {isSingleDelete ? "Delete Policy" : "Bulk Delete"}
                <IconButton
                    onClick={handleClose}
                    sx={{ marginLeft: "auto" }}
                    size="small"
                    aria-label="close"
                >
                    <CloseIcon />
                </IconButton>
            </>);
        }
    }

    const getHeaderSubTitle = () => {
        if (actionType === PIPActionType.BULK_HOLD) return ""
        else if (actionType === PIPActionType.BULK_RETURN) return `${recordCount} record${recordCount !== 1 ? "s" : ""} Selected`
        else if (actionType === PIPActionType.BULK_DELETE) return isSingleDelete ? "" : `${recordCount} record${recordCount !== 1 ? "s" : ""} Selected`
    }

    const getTextFieldLabel = () => {
        if (actionType === PIPActionType.BULK_HOLD) return (<>Reason for Hold <span>*</span></>)
        else if (actionType === PIPActionType.BULK_RETURN) return (<>REASON FOR RETURN <span>*</span></>)
        else if (actionType === PIPActionType.BULK_DELETE) return (<>REASON FOR DELETION <span>*</span></>)
    }

    const getTextFieldPlaceholder = () => {
        if (actionType === PIPActionType.BULK_HOLD) return "eg. Awaiting county clarification on legal description"
        else if (actionType === PIPActionType.BULK_RETURN) return `Enter the reason for returning the record${recordCount !== 1 ? "s" : ""} to ${returnTabName}`
        else if (actionType === PIPActionType.BULK_DELETE) return `Enter the reason for deleting the record${recordCount !== 1 ? "s" : ""}`
    }

    const getDialogContent = () => {
        if (actionType === PIPActionType.BULK_HOLD || isRemoveHold) return (
            <>
                <Typography
                    sx={{
                        fontSize: fontSize.base,
                        fontWeight: fontWeight.bold2,
                        color: colors.black01,
                        mb: gaps.medium1,
                    }}
                >
                    {isRemoveHold
                        ? "Remove the hold and resume workflow promotion?"
                        : isSingleHold
                        ? "Hold this record and stop workflow promotion?"
                        : `Hold ${recordCount} selected ${recordCount !== 1 ? "policies" : "policy"} and stop workflow promotion?`}
                </Typography>
                <RadioGroup
                    value={holdScope}
                    onChange={(e) => setHoldScope(e.target.value as HOLD_SCOPE)}
                    sx={{ mb: gaps.medium1 }}
                >
                    <FormControlLabel
                        value={HOLD_SCOPE.ALL_MATCHING}
                        control={<Radio />}
                        sx={{
                            alignItems: "center",
                            m: 0,
                            p: `${padding.small1} ${padding.medium1}`,
                            border: holdScope === HOLD_SCOPE.ALL_MATCHING ? "1px solid #b6cfe4" : "1px solid transparent",
                            borderRadius: borderRadius.small,
                            backgroundColor: holdScope === HOLD_SCOPE.ALL_MATCHING ? "#eaf3fb" : "transparent",
                            mb: gaps.small1,
                        }}
                        label={
                            <Box>
                                <Typography component="span" sx={{ fontWeight: fontWeight.bold2, fontSize: fontSize.small, color: colors.black01 }}>
                                    All matching records{" "}
                                </Typography>
                                <Typography component="span" sx={{ fontWeight: fontWeight.normal2, fontSize: fontSize.small, color: colors.grey11 }}>
                                    Hold this record and every other record with the same Agent ID and File Name/Number, across Quality Review and Pre-Close Queue.
                                </Typography>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value={HOLD_SCOPE.SELECTED}
                        control={<Radio />}
                        sx={{
                            alignItems: "center",
                            m: 0,
                            p: `${padding.small1} ${padding.medium1}`,
                            borderRadius: borderRadius.small,
                            border: holdScope === HOLD_SCOPE.SELECTED ? "1px solid #b6cfe4" : "1px solid transparent",
                            backgroundColor: holdScope === HOLD_SCOPE.SELECTED ? "#eaf3fb" : "transparent",
                        }}
                        label={
                            <Box>
                                <Typography component="span" sx={{ fontWeight: fontWeight.bold2, fontSize: fontSize.small, color: colors.black01 }}>
                                    This record only{" "}
                                </Typography>
                                <Typography component="span" sx={{ fontWeight: fontWeight.normal2, fontSize: fontSize.small, color: colors.grey11 }}>
                                    Hold just this policy. Don&apos;t affect any other records.
                                </Typography>
                            </Box>
                        }
                    />
                </RadioGroup>
                {isRemoveHold && (
                    <Typography
                        sx={{
                            fontSize: fontSize.small,
                            color: colors.grey11,
                            mt: gaps.small1,
                        }}
                    >
                        {infoMessage}
                    </Typography>
                )}
            </>
        )
        else if (actionType === PIPActionType.BULK_RETURN) return (<> <WarningBanner>
            <WarningAmberIcon sx={{ color: "#c49a24", fontSize: 20, mt: "2px" }} />
            <Typography sx={{ fontSize: fontSize.small, color: colors.yellow01 }}>
                Records will leave {tabName} immediately and require re-validation to advance again. <b>IsReadyToPreClose</b> and <b>IsReadyToClose</b> will be reset.
            </Typography>
        </WarningBanner></>)
        else if (actionType === PIPActionType.BULK_DELETE) return (<> <WarningBanner>
            <WarningAmberIcon sx={{ color: "#c49a24", fontSize: 20, mt: "2px" }} />
            <Typography sx={{ fontSize: fontSize.small, color: colors.yellow01 }}>
                {isSingleDelete
                    ? "This record will be removed from the queue immediately. It can be undone by restoring the record."
                    : `${recordCount} selected record${recordCount !== 1 ? "s" : ""} will be removed from the queue immediately. This can be undone by restoring ${recordCount !== 1 ? "them" : "it"}.`}
            </Typography>
        </WarningBanner></>)
    }

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: borderRadius.medium } }}
        >
            <DialogHeading>
                {getHeaderLabel()}
            </DialogHeading>

            {showPolicyNumber ? (
                <Subtitle
                    sx={{
                        paddingLeft: padding.xlarge1,
                        marginTop: 0,
                        marginBottom: gaps.medium1,
                    }}
                >
                    Policy #:{" "}
                    <Box
                        component="span"
                        sx={{ fontWeight: fontWeight.bold2, color: colors.black01 }}
                    >
                        {policyNumber}
                    </Box>
                </Subtitle>
            ) : (
                <Subtitle>{getHeaderSubTitle()}</Subtitle>
            )}

            <DialogContent sx={{ paddingTop: 0 }}>

                {getDialogContent()}

                {!isRemoveHold && (
                    <>
                        <FieldLabel>
                            {getTextFieldLabel()}
                        </FieldLabel>
                        <TextField
                            multiline
                            minRows={3}
                            fullWidth
                            placeholder={getTextFieldPlaceholder()}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                        />
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ padding: padding.large1, gap: gaps.small1 }}>
                <ActionButton variant="outlined" color="secondary" disabled={isSubmitting} onClick={handleClose}>
                    Cancel
                </ActionButton>
                <ActionButton
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting || (!isRemoveHold && reason.trim().length === 0)}
                    onClick={handleConfirm}
                >
                    {isSingleHold || isRemoveHold || isSingleDelete ? buttonLabel : `${buttonLabel} (${recordCount})`}
                </ActionButton>
            </DialogActions>
        </Dialog>
    );
};

export default BulkActionModal;
